import * as React from 'react';
import { delayPromise } from '../utilities/DelayPromise';

import { assertUnreachable } from '../utilities/TypeScriptHelpers';
import { MergeOrUpdateStateAction, setStateArgForMergeableState } from './useMergeableState';

export const INLINE_EDITOR_DELAY_AFTER_SAVING_MILLIS = 100;

/** Params for the hook, to be supplied by the component that uses it */
export type InlineEditorParams<S> = {

  /**
   * Initial state for the editor when entering edit mode. This is the data that gets sent to onSave and onCancel.
   *
   * The intended way to use this hook is to render a controlled component in edit mode, whose state
   * (the `editorState`) is managed by this hook. Hence, any time the state is to change, it should be updated
   * by calling `setEditorStateWithinEditMode`. This pattern allows the Save button to have access
   * to the most current data.
   *
   * **The editor state is valid only when in edit mode!** Upon leaving edit mode, the editor state
   * is destroyed, and it is up to the component to hold on to the necessary state in onSave.
   * Each time the editor enters edit mode, the state gets reïnitialized to the initial value,
   * so, **in order to display the "new value" when reëntering edit mode after saving,
   * you must update the `editorStateWhenEnteringEditMode` param sent to the hook**.
   *
   * In some cases, it may be convenient or necessary to synchronize state updates by storing _all_
   * the editor component's state in `editorState`, even if only some of it is needed by onSave.
   */
  readonly editorStateWhenEnteringEditMode: S;

  /**
   * This callback gets called when the save button is pressed, passing the editorState back to the
   * component so it can be saved.
   *
   * - Upon resolution of the promise, the logic will transition back to Readonly Mode.
   * - If the promise was rejected, the logic will stay in Edit Mode and reënable the save/cancel buttons,
   *   or transition back to Readonly Mode if `exitEditModeOnFailedSave` is true.
   */
  readonly onSave: (editorState: S) => Promise<void>;

  /**
   * This optional callback gets called when the cancel button is pressed, to support rare use cases
   * where the client needs to do some cleanup when the user leaves edit mode without saving.
   */
  readonly onCancel?: (editorState: S) => void;

  /**
   * Whether or not edit mode can be entered at all.
   * (In some cases, editing may be conditionally disabled)
   */
  readonly canEdit: boolean;

  /**
   * Callback to determine whether saving should be allowed based on the editor state.
   * This is often connected to a validation check on the editor state.
   *
   * If not defined, saving is always enabled.
   */
  readonly canSaveFn?: (editorState: S) => boolean;

  /**
   * If true, will discard the editor state and leave edit mode on a failed save.
   * Otherwise, will stay in edit mode with the latest editor state.
   *
   * Defaults to false, to allow the user to try again.
   */
  readonly exitEditModeOnFailedSave?: boolean;

  /**
   * By default, the edit button will get focus when the user leaves edit mode,
   * as long as the provided edit button component is used (or the `editButtonRef`)
   * is passed into a custom edit button.
   *
   * If this behavior is not desired, set this parameter to true.
   */
  readonly disableFocusEditButtonOnExitEditMode?: boolean;
};

/**
 * The stuff that the hook returns to the component.
 *
 * The logic has different stuff depending on whether it's in Readonly Mode or Edit Mode.
 * Use the provided type guard functions `isEditMode` and `isReadonlyMode` to allow TypeScript
 * to distinguish between the different modes and get access to the relevant members.
 */
export type InlineEditorLogic<S> = InlineEditorLogic_ReadonlyMode | InlineEditorLogic_EditMode<S>;

export type InlineEditorLogic_ReadonlyMode = {

  /** Iff we are in edit mode. If `canEdit` is false, this will always be false. */
  readonly inEditMode: false;

  /** Function to enter edit mode, equivalent to clicking the edit button. If canEdit is false, this is a noöp. */
  readonly enterEditMode: () => void;

  /** The value of the `canEdit` param, passed through for convenience */
  readonly canEdit: boolean;

  /** Ref to attach to the edit button, to focus it after we leave edit mode */
  readonly editButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
};

export type InlineEditorLogic_EditMode<S> = {

  /** Iff we are in edit mode. */
  readonly inEditMode: true;

  /**
   * The current editor state, valid only when in edit mode.
   * If in readonly mode, the client should render a readonly version of the data
   * (probably the same value that populates editorStateWhenEnteringEditMode).
   */
  readonly editorState: S;

  /**
   * Callback to update the editor state, valid only when in edit mode.
   * Keep in mind that the editor state gets destroyed when leaving edit mode.
   *
   * As a convenience to the user, because all the state must be in this object, this function
   * supports merging partial state objects with `useMergeableState` (arrays and functions are not
   * supported). If the editor state contains multiple interdependent pieces, the client can use
   * `useSetStateAsReducer` to manage it with a reducer.
   *
   * If EditorState contains multiple properties, it may be helpful to pass this handle into the
   * useSetStateAsReducer hook, which will allow the state to be updated via a reducer.
   */
  readonly setEditorStateWithinEditMode: React.Dispatch<MergeOrUpdateStateAction<S>>;

  /** True when saving is in progress */
  readonly isActionBusy: boolean;

  /** Iff saving is allowed (this is the result of running `canSaveFn` on the current editor state */
  readonly canSaveThisEditorState: boolean;

  /**
   * Function to begin the save process, using the current editorState. If canSave evaluates to
   * false, this will have no effect.
   */
  readonly startSave: () => void;

  /** Function to leave edit mode and go back to readonly mode */
  readonly cancelEditing: () => void;
};

type ReducerAction<S> =
  | {
    readonly type: 'start-editing' | 'cancel-editing' | 'start-save' | 'abort-save' | 'finish-save'
  }
  | {
    readonly type: 'set-editor-state-within-edit-mode';
    readonly nextEditorState: MergeOrUpdateStateAction<S>;
  };

type ReducerState<S> =
  | {
    name: 'readonlyMode';
  }
  | {
    name: 'editMode_editing';
    editorState: S;
  }
  | {
    name: 'editMode_saving';
    editorState: S;
  };

/**
 * This custom implementation of side effects is cumbersome and we SHOULD NOT COPY
 * THIS PATTERN ELSEWHERE. Use 3rd-party use-effect-reducer hook instead.
 *
 * The way this works is that the state can return the side effects object, which
 * lists which side effects should happen on that transition, and their respective
 * parameters if applicable. Those effects are then run in a useEffect block.
 */
type SideEffects<S> = {
  onSave?: Parameters<Required<InlineEditorParams<S>>['onSave']>;
  onCancel?: Parameters<Required<InlineEditorParams<S>>['onCancel']>;
  focusEditButton?: true; // side effect has no parameters
}

type ReducerStateWithSideEffects<S> = ReducerState<S> & { sideEffects?: SideEffects<S> }
type ReducerType<S> = React.Reducer<ReducerStateWithSideEffects<S>, ReducerAction<S>>;


/**
 * The state machine models transitions of the form:
 *
 *     previousState + action = nextState + side effects
 *
 * We can make a simple type-safe implementation using useReducer, but if it gets any more
 * complicated (e.g. with more side effects, more state nodes, or async actions), we should consider
 * switching the implementation to a library such as robot3 or xstate.
 * @param params
 */
function makeStateMachine<S>(
  params: Pick<InlineEditorParams<S>, 'canEdit' | 'canSaveFn' | 'editorStateWhenEnteringEditMode' | 'exitEditModeOnFailedSave'>
): ReducerType<S> {
  const { canEdit, canSaveFn, editorStateWhenEnteringEditMode, exitEditModeOnFailedSave } = params;

  return function inlineEditorStateMachine(
    prevState: ReducerStateWithSideEffects<S>,
    action: ReducerAction<S>
  ): ReducerStateWithSideEffects<S> {
    switch (prevState.name) {
      case 'readonlyMode':
        switch (action.type) {
          case 'start-editing':
            if (canEdit) {
              return {
                name: 'editMode_editing',
                editorState: editorStateWhenEnteringEditMode,
              };
            } else {
              return prevState;
            }
          default:
            return prevState;
        }

      case 'editMode_editing':
        switch (action.type) {
          case 'set-editor-state-within-edit-mode': {
            const nextStateOrUpdaterFn = setStateArgForMergeableState(action.nextEditorState);
            const nextEditorState =
              typeof nextStateOrUpdaterFn === 'function'
                ? (nextStateOrUpdaterFn as (s: S) => S)(prevState.editorState)
                : nextStateOrUpdaterFn;
            return { ...prevState, editorState: nextEditorState };
          }
          case 'start-save':
            if (canSaveFn ? canSaveFn(prevState.editorState) : true) {
              return {
                name: 'editMode_saving',
                editorState: prevState.editorState,
                sideEffects: { onSave: [prevState.editorState] }
              };
            } else {
              return prevState;
            }
          case 'cancel-editing':
            return {
              name: 'readonlyMode',
              sideEffects: {
                onCancel: [prevState.editorState],
                focusEditButton: true,
              }
            };
          default:
            return prevState;
        }

      case 'editMode_saving':
        switch (action.type) {
          case 'abort-save':
            if (exitEditModeOnFailedSave) {
              // It might make sense to invoke the onCancel side effect here too, but the previous
              // implementation didn't, so we're keeping it the same until someone needs it.
              return {
                name: 'readonlyMode',
                sideEffects: { focusEditButton: true }
              };
            } else {
              return {
                name: 'editMode_editing',
                editorState: prevState.editorState,
              };
            }
          case 'finish-save':
            return {
              name: 'readonlyMode',
              sideEffects: { focusEditButton: true }
            };
          default:
            return prevState;
        }

      default:
        assertUnreachable(prevState);
        return prevState;
    }
  }
}

/**
 * The useInlineEditor hook provides state-handling logic for an inline editor scenario
 * (examples: rename a content item or edit its description on its place page, rather than a dedicated modal dialog).
 *
 * This editor allows for two modes:
 *
 * - Readonly mode: Show the data in a readonly mode, with a button that, when clicked, enters edit mode
 * - Edit mode: Provide widgets that allow the user to edit the data, with a save button that, when clicked,
 *   sends the new data to the onSave callback provided by the client.
 *
 * This hook provides the required state, callbacks, and button components to facilitate these two modes,
 * but the client component is expected to provide their actual layout/implementation.
 * See the unit tests in useInlineEditor.test.tsx for a simple usage example.
 *
 * @param params The params passed into the hook. See documentation for InlineEditorParams.
 */
export function useInlineEditor<S>(params: InlineEditorParams<S>): InlineEditorLogic<S> {

  const editButtonRef = React.useRef<HTMLButtonElement>(null);

  const stateMachine = React.useMemo(
    () => makeStateMachine({
      canEdit: params.canEdit,
      editorStateWhenEnteringEditMode: params.editorStateWhenEnteringEditMode,
      exitEditModeOnFailedSave: params.exitEditModeOnFailedSave,
    }),
    [params.canEdit, params.editorStateWhenEnteringEditMode, params.exitEditModeOnFailedSave]
  );

  const [state, dispatch] = React.useReducer(stateMachine, {
    name: 'readonlyMode',
  });

  // Execute the prescribed side effects from the state machine, if the current state has any
  React.useEffect(
    () => {

      // This pattern carries a theoretical risk of the props changing, causing the side effects in
      // the deps array to change, thus invalidating the async side effect before it makes its final
      // dispatch. In practice, this is not a problem: props may change, causing the state machine
      // to be rebuilt, but the state itself does not change until another action is dispatched to
      // it, so the side effects don't change unless the state machine actually transitions.
      //
      // This should continue to be okay for us as long as we don't have any side effects that
      // expect to asynchronously dispatch actions to a different state than the one from which they
      // were invoked.
      let isValid = true;

      async function applyEffects() {
        if (state.sideEffects?.onSave) {
          const onSaveParams = state.sideEffects.onSave;
          try {
            await params.onSave(...onSaveParams);
            await delayPromise(INLINE_EDITOR_DELAY_AFTER_SAVING_MILLIS);
            if (isValid) {
              dispatch({ type: 'finish-save' });
            }
          } catch (e) {
            if (isValid) {
              dispatch({ type: 'abort-save' });
            }
          }
        }

        if (state.sideEffects?.onCancel && params.onCancel) {
          params.onCancel(...state.sideEffects.onCancel);
        }

        if (state.sideEffects?.focusEditButton && !params.disableFocusEditButtonOnExitEditMode) {
          if (editButtonRef.current) {
            editButtonRef.current.focus();
          }
        }
      }

      void applyEffects();

      return () => { isValid = false };
    },
    // This deps array was noncompliant before we enabled the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.sideEffects]
  );

  const setEditorStateWithinEditMode = React.useCallback(
    (nextEditorState: MergeOrUpdateStateAction<S>) => dispatch({ type: 'set-editor-state-within-edit-mode', nextEditorState }),
    []
  );

  if (state.name === 'readonlyMode') {
    return {
      inEditMode: false,
      canEdit: params.canEdit,
      enterEditMode: () => dispatch({ type: 'start-editing' }),
      editButtonRef,
    };
  } else {
    return {
      editorState: state.editorState,
      setEditorStateWithinEditMode,
      isActionBusy: state.name === 'editMode_saving',
      inEditMode: true,
      canSaveThisEditorState: params.canSaveFn ? params.canSaveFn(state.editorState) : true,
      startSave: () => dispatch({ type: 'start-save' }),
      cancelEditing: () => dispatch({ type: 'cancel-editing' }),
    };
  }
}

export function isEditMode<S>(logic: InlineEditorLogic<S>): logic is InlineEditorLogic_EditMode<S> {
  return logic.inEditMode;
}

export function isReadonlyMode<S>(logic: InlineEditorLogic<S>): logic is InlineEditorLogic_ReadonlyMode {
  return !logic.inEditMode;
}
